import {
  useAlarmUpdates,
  useSharedRawAlarmUpdates,
} from '@hakimo-ui/hakimo/data-access';
import {
  LocationAlarmUpdate,
  LocationAlarmUpdatesDTO,
  UnifiedAlarm,
} from '@hakimo-ui/hakimo/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ReactElement } from 'react';
import AlarmStatusPanel from '../alarm-details/alarm-status/AlarmStatus';

dayjs.extend(utc);

const REFETCH_INTERVAL = 60000;

interface Props {
  event: UnifiedAlarm;
  sharedToken?: string;
}

export function ScanRawAlarmStatus(props: Props) {
  const { event, sharedToken } = props;

  const renderPanel = (updates?: LocationAlarmUpdatesDTO) => (
    <AlarmStatusPanel event={event} updates={updates} />
  );

  if (sharedToken) {
    return (
      <SharedUpdatesFetcher sharedToken={sharedToken}>
        {renderPanel}
      </SharedUpdatesFetcher>
    );
  }

  return (
    <RegularUpdatesFetcher
      eventId={event.id}
      timestamp={String(event.alarmStartTime)}
    >
      {renderPanel}
    </RegularUpdatesFetcher>
  );
}

export default ScanRawAlarmStatus;

interface RegularUpdatesFetcherProps {
  eventId: string;
  timestamp: string;
  children: (updates?: LocationAlarmUpdatesDTO) => ReactElement;
}

function RegularUpdatesFetcher(props: RegularUpdatesFetcherProps) {
  const { eventId, timestamp, children } = props;

  const searchParams = new URLSearchParams();
  searchParams.append('product', 'scan');
  if (timestamp) {
    const date = dayjs.utc(timestamp);
    const partitionKey = date.format('YYYYMM');
    searchParams.append('partitionKey', partitionKey);
  }

  const { data } = useAlarmUpdates(
    eventId,
    searchParams.toString(),
    REFETCH_INTERVAL
  );

  const updates: LocationAlarmUpdatesDTO | undefined = data && {
    items: data.items.map((u) => ({
      update_time: u.update_time,
      update_type: u.update_type,
      user: u.user,
      recommendation: u.recommendation,
      summary: u.summary,
      event_id: u.event_id,
    })) as LocationAlarmUpdate[],
  };

  return children(updates);
}

interface SharedUpdatesFetcherProps {
  sharedToken: string;
  children: (updates?: LocationAlarmUpdatesDTO) => ReactElement;
}

function SharedUpdatesFetcher(props: SharedUpdatesFetcherProps) {
  const { sharedToken, children } = props;
  const { data } = useSharedRawAlarmUpdates(sharedToken);
  return children(data);
}
